import { 
  BarChart3, 
  FileText, 
  Shield, 
  Users, 
  Settings,
  Home,
  MessageSquare,
  Mail,
  UserCheck,
  Building,
  BookOpen,
  Upload,
  DollarSign,
  Link2,
  Percent,
  Banknote,
  PenTool,
  Activity,
  Heart,
  Circle
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

const navigationItems = [
  {
    title: 'Dashboard',
    url: '/admin',
    icon: Home,
    exact: true,
  },
  {
    title: 'Content',
    url: '/admin/content',
    icon: PenTool,
  },
  {
    title: 'Bulk Import',
    url: '/admin/content/blog/import',
    icon: Upload,
  },
  {
    title: 'Analytics',
    url: '/admin/analytics',
    icon: BarChart3,
  },
];

const appItems = [
  {
    title: 'App Dashboard',
    url: '/admin/app',
    icon: Activity,
  },
];

const formsItems = [
  {
    title: 'Contact Submissions',
    url: '/admin/forms/contact',
    icon: MessageSquare,
  },
  {
    title: 'Early Adopters',
    url: '/admin/forms/early-adopters',
    icon: UserCheck,
  },
  {
    title: 'Newsletter Signups',
    url: '/admin/forms/newsletter',
    icon: Mail,
  },
  {
    title: 'Professional Network Apps',
    url: '/admin/forms/professionals',
    icon: Users,
  },
  {
    title: 'Sponsor Inquiries',
    url: '/admin/forms/sponsors',
    icon: Building,
  },
  {
    title: 'Story Submissions',
    url: '/admin/forms/stories',
    icon: BookOpen,
  },
];

const referralItems = [
  {
    title: 'Referral Sources',
    url: '/admin/referrals/sources',
    icon: Link2,
  },
  {
    title: 'Referral Events',
    url: '/admin/referrals/events',
    icon: DollarSign,
  },
  {
    title: 'Rate Tables',
    url: '/admin/referrals/rates',
    icon: Percent,
  },
  {
    title: 'Payouts',
    url: '/admin/referrals/payouts',
    icon: Banknote,
  },
];

const systemItems = [
  {
    title: 'SEO Health',
    url: '/admin/seo',
    icon: FileText,
  },
  {
    title: 'Form Testing',
    url: '/admin/form-testing',
    icon: MessageSquare,
  },
  {
    title: 'Import Professionals',
    url: '/admin/import',
    icon: Upload,
  },
  {
    title: 'Admin Users',
    url: '/admin/users',
    icon: Shield,
  },
  {
    title: 'Settings',
    url: '/admin/settings',
    icon: Settings,
  },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path: string, exact = false) => 
    exact ? currentPath === path : currentPath.startsWith(path);

  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/50';

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-64'} collapsible="icon">
      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={item.url} 
                      end={item.exact}
                      className={getNavCls}
                    >
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* SeniorThrive App */}
        <SidebarGroup>
          <SidebarGroupLabel>SeniorThrive App</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {appItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Forms Management */}
        <SidebarGroup>
          <SidebarGroupLabel>Form Submissions</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {formsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Referral Management */}
        <SidebarGroup>
          <SidebarGroupLabel>Referrals</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {referralItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* System */}
        <SidebarGroup>
          <SidebarGroupLabel>System</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {systemItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}